import { AlertTriangle, CheckCircle2, CircleHelp, CircleSlash, Images, RefreshCw, RotateCcw, ScanBarcode, ScanSearch, Sparkles, VideoOff, WifiOff } from 'lucide-react'
import { useRef, useState, useSyncExternalStore, type ChangeEvent, type ReactElement, type ReactNode } from 'react'

import { ActionInfo, ActionInfoQuiet, Button, PhotoViewer, ScreenInfo, StatusMessage, useHasCamera } from '@krp/ui-kit'

import '../../../shared/platform.css'
import { photoProblem } from '../model/photo'
import type { ScanSessionVm } from '../../scan-session/model/types'
import { ScanSessionBar } from '../../scan-session/ui/ScanSessionBar'
import { searchRun } from '../model/searchRun'
import type { HealthLevel, OpenCommonProduct, PhotoRecognition, ProductPhotoUrl, SearchActions, SearchOutcomeVm, SearchVm, VerdictActions } from '../model/types'
import { preparePhoto } from './preparePhoto'
import { ScanVerdictPanel } from './ScanVerdictPanel'
import { ReadTextBlock, SearchDoneLine, SearchProgressPanel } from './SearchProgressPanel'

/** Почему «Сканер» выключен без камеры — для ⓘ. */
const NO_CAMERA_REASON = 'На этом устройстве нет камеры — выберите фото.'

/** Справка экрана — единственная ⓘ, сверху слева: коротко и обычными словами (решение владельца 22.10.2026). */
export const SEARCH_SCREEN_INFO = {
  title: 'Поиск по фото',
  lines: [
    'Снимите товар или выберите фото — поиск начнётся сам.',
    'Ответ: «Это он?» — почти уверены; «Выберите вариант» — несколько почти одинаковых товаров (например, серия «Fix» и «Fix 2»): сравните отличия, выделенные в названиях, и выберите свой; «Возможно, это» — похожие варианты; «Нет в базе» — такого товара нет, его можно создать или прикрепить фото к существующему; «Не удалось определить» — переснимите, это не значит, что товара нет.',
    'Число в углу карточки — процент совпадения со снимком, у вариантов он округлён до 5. «Из чего сложилось» показывает, что говорят слова, числа, штрихкод и фото: «за», «против» или «не повлияло»; «нет данных» — этой проверки не было, это не минус. «Фото проверено» — фото товара сравнили со снимком, «не проверено» — сходства по фото нет (фото в базе нет или оно сильно отличается). Если текст и фото указывают на разные товары, об этом сказано под названием.',
    'Под ответом всегда список лучших кандидатов: что ниже порога, подписано «вероятнее всего не он», но выбрать такой товар можно самому. Сбой поиска — нажмите «Повторить» или выберите другое фото.',
  ],
} as const

const HEALTH_ICON: Record<HealthLevel, ReactElement> = {
  unknown: <CircleHelp size={14} aria-hidden="true" />,
  checking: <RefreshCw size={14} aria-hidden="true" />,
  ok: <CheckCircle2 size={14} aria-hidden="true" />,
  degraded: <AlertTriangle size={14} aria-hidden="true" />,
  'not-configured': <CircleSlash size={14} aria-hidden="true" />,
  unreachable: <WifiOff size={14} aria-hidden="true" />,
  forbidden: <CircleSlash size={14} aria-hidden="true" />,
}

/**
 * Что ушло в поиск — вверху выдачи (RP-FB26): СЛЕВА миниатюра отправленного снимка (нажатие — на весь экран, видно, что
 * снялось и какого качества кадр), СПРАВА прочитанный на нём текст «Текст на фото» (решение владельца 23.10.2026: не прячется,
 * компактно, мелким шрифтом, целиком и без прокрутки). Сбой поиска прочитанного не несёт — тогда только снимок.
 */
function SentShot({ url, recognized, large = false }: { url: string | null; recognized?: PhotoRecognition | null; large?: boolean }) {
  const [open, setOpen] = useState<string | null>(null)
  if (url === null && recognized === undefined) return null
  return (
    <div className="platform-search__sent" role="group" aria-label="Что ушло в поиск" data-size={large ? 'large' : undefined}>
      {url !== null && (
        <>
          <ActionInfo id="platform.search.sent-shot" label="Отправленный снимок">
            <button
              type="button"
              className="platform-search__sent-shot"
              aria-label="Снимок на весь экран"
              aria-haspopup="dialog"
              onClick={() => setOpen(url)}
            >
              <img src={url} alt="" />
            </button>
          </ActionInfo>
          {open === url && <PhotoViewer src={url} alt="Снимок, отправленный в поиск" caption="Снимок, отправленный в поиск" onClose={() => setOpen(null)} />}
        </>
      )}
      {recognized === null && <p className="platform-sync__note">Текст на фото не прочитан: служба распознавания текста не ответила.</p>}
      {recognized !== undefined && recognized !== null && <ReadTextBlock recognized={recognized} />}
    </div>
  )
}

/** Что делать дальше после ошибки или пустого итога: тот же кадр ещё раз или другой снимок. Ничего не исчезает само. */
function RetryBar({ onRetry, onPickAnother }: { onRetry?: (() => void) | undefined; onPickAnother?: (() => void) | undefined }) {
  if (onRetry === undefined && onPickAnother === undefined) return null
  return (
    <div className="platform-search__retry" role="group" aria-label="Что дальше">
      {onRetry !== undefined && (
        <Button variant="primary" icon={<RotateCcw size={16} aria-hidden="true" />} onClick={onRetry} actionId="platform.search.retry">
          Повторить
        </Button>
      )}
      {onPickAnother !== undefined && (
        <Button variant="secondary" icon={<Images size={16} aria-hidden="true" />} onClick={onPickAnother} actionId="platform.search.pick-another">
          Выбрать другое фото
        </Button>
      )}
    </div>
  )
}

/**
 * Пометка над выдачей, пока сервер её уточняет: список уже виден, но он предварительный. Идёт работа — без процентов, значок + слова + бегущая точка.
 * Список собран только по тексту и штрихкоду, фото ещё считается — «Уточняется по фото»; фото готово, а ИИ сверяет похожие варианты — «ИИ сравнивает кандидатов».
 */
function ComparingNote() {
  const refining = useSyncExternalStore(searchRun.subscribe, () => searchRun.get().preliminaryRefining)
  if (refining) {
    return (
      <p className="platform-search__comparing" role="status" aria-live="polite" data-state="refining">
        <ScanSearch size={16} aria-hidden="true" />
        <strong>Уточняется по фото</strong>
        <span className="platform-search__comparing-dot" aria-hidden="true" />
        <span>Список собран по тексту и штрихкоду, фото ещё проверяется: он может измениться.</span>
      </p>
    )
  }
  return (
    <p className="platform-search__comparing" role="status" aria-live="polite" data-state="comparing">
      <Sparkles size={16} aria-hidden="true" />
      <strong>ИИ сравнивает кандидатов</strong>
      <span className="platform-search__comparing-dot" aria-hidden="true" />
      <span>Это предварительная выдача, она может измениться.</span>
    </p>
  )
}

/**
 * Итог после предварительной выдачи: сверка ИИ закончилась — «выдача уточнена»; не вышла — выдача осталась предварительной, причина дословно из этапа
 * «Проверка». Сверки не было (предварительная выдача ушла по тексту, а итог по фото однозначный) — «уточнена по фото». Без предварительной выдачи пометки нет.
 */
function RefinementNote() {
  const run = useSyncExternalStore(searchRun.subscribe, searchRun.get)
  if (run.preliminary === null || run.phase !== 'done') return null
  const judge = run.stages.judge
  if (judge.state !== 'failed') {
    return (
      <p className="platform-search__comparing" role="status" data-state="refined">
        <CheckCircle2 size={16} aria-hidden="true" />
        <strong>{judge.state === 'done' ? 'Выдача уточнена' : 'Выдача уточнена по фото'}</strong>
        <span>{judge.state === 'done' ? 'ИИ сравнил кандидатов со снимком.' : 'Список сверен со снимком.'}</span>
      </p>
    )
  }
  return (
    <p className="platform-search__comparing" role="status" data-state="unrefined">
      <AlertTriangle size={16} aria-hidden="true" />
      <strong>Выдача не уточнена</strong>
      <span>{judge.note ?? 'Сверка ИИ не выполнена.'}</span>
    </p>
  )
}

/**
 * Один ключ у панели ответа во всех состояниях выдачи (идёт поиск с предварительным списком → итог → сбой после предварительного): React сопоставляет детей по ключу,
 * поэтому панель не пересоздаётся, когда ранний список заменяется следующим или итогом, и выбор человека («Нет, другой», «Ничего из этого») не сбрасывается.
 */
const VERDICT_PANEL_KEY = 'verdict-panel'

/** Выдача поиска по фото: на экране поиска и под видоискателем сканера (RP-FB4). */
export function SearchOutcome({
  outcome,
  photoUrl,
  onOpenProduct,
  sentShot = null,
  onRetry,
  onPickAnother,
  canSeeDetails = false,
  detailsExtra = null,
  verdictActions,
  onRetake,
  retakeLabel = 'Выбрать другое фото',
}: {
  /** Действия над ответом с вердиктом (RP-FB56); не заданы — ответ показан без кнопок «Открыть/Создать/Прикрепить». */
  verdictActions?: VerdictActions | undefined
  /** «Снять ещё раз» / «Выбрать другое фото» под ответом с вердиктом; не задано — кнопки нет. */
  onRetake?: (() => void) | undefined
  retakeLabel?: string | undefined
  /** Видна ли администратору системы ссылка «Посмотреть детали поиска» (с «Как обработано фото» внутри) в строке итога; не задано — ссылки нет. */
  canSeeDetails?: boolean | undefined
  /** Узел внутри раскрытых деталей поиска (время по этапам): данные приносит приложение; не задан — детали без него. */
  detailsExtra?: ReactNode
  /** Повторить поиск по тому же кадру; не задано - кнопки нет. */
  onRetry?: (() => void) | undefined
  /** Выбрать другой снимок; не задано - кнопки нет. */
  onPickAnother?: (() => void) | undefined
  outcome: SearchOutcomeVm
  photoUrl: ProductPhotoUrl | undefined
  /** Открыть ОБЩУЮ карточку найденного товара (по идентификатору общего товара из ответа). */
  onOpenProduct: OpenCommonProduct
  /** Адрес отправленного снимка; нет — выдача без него. */
  sentShot?: string | null | undefined
}) {
  switch (outcome.kind) {
    case 'idle':
      return <p className="platform-sync__note">Снимите или выберите фото товара — поиск начнётся сразу.</p>
    case 'searching':
      // Ход поиска показывает панель хода (снимок, кольцо, шаги) — второй индикатор рядом не нужен. Предварительная выдача (список до сверки ИИ) уже видна:
      // без кнопок действий — выбирать товар имеет смысл по итогу.
      if (outcome.preliminary === undefined) return null
      return (
        <>
          <ComparingNote />
          <ScanVerdictPanel key={VERDICT_PANEL_KEY} outcome={outcome.preliminary} photoUrl={photoUrl} onOpenProduct={onOpenProduct} actions={undefined} retakeLabel={retakeLabel} onRetake={undefined} />
        </>
      )
    case 'cancelled':
      return (
        <>
          <SearchDoneLine canSeeDetails={canSeeDetails} shotUrl={sentShot} detailsExtra={detailsExtra} />
          <StatusMessage kind="empty" title="Поиск отменён" text="Вы остановили поиск: результата нет, это не значит, что товара нет." />
          <RetryBar onRetry={onRetry} onPickAnother={onPickAnother} />
        </>
      )
    case 'verdict':
      return (
        <>
          {/* Порядок владельца (23.10.2026) и при вердикте: снимок и текст → «Найдено за N с» → подсказка вердикта → «Найдено: N» → список. */}
          <SentShot url={sentShot} recognized={outcome.recognized} />
          <SearchDoneLine canSeeDetails={canSeeDetails} shotUrl={sentShot} detailsExtra={detailsExtra} />
          <RefinementNote />
          <ScanVerdictPanel
            key={VERDICT_PANEL_KEY}
            outcome={outcome}
            photoUrl={photoUrl}
            onOpenProduct={onOpenProduct}
            actions={verdictActions}
            retakeLabel={retakeLabel}
            onRetake={onRetake}
          />
          {outcome.unresolved > 0 && <UnresolvedNote count={outcome.unresolved} />}
        </>
      )
    case 'failed':
      return (
        <>
          <SearchDoneLine canSeeDetails={canSeeDetails} shotUrl={sentShot} detailsExtra={detailsExtra} />
          <SentShot url={sentShot} large />
          <StatusMessage
            kind={outcome.degraded ? 'unavailable' : outcome.title === 'Нет доступа к поиску' ? 'forbidden' : 'error'}
            title={outcome.title}
            text={outcome.message}
            meta={`Код: ${outcome.code}${outcome.correlationId === null ? '' : ` · запрос ${outcome.correlationId}`}`}
          />
          {outcome.degraded && <p className="platform-sync__note">Это не значит, что товара нет.</p>}
          {outcome.preliminary !== undefined && (
            <>
              <p className="platform-search__comparing" role="status" data-state="unrefined">
                <AlertTriangle size={16} aria-hidden="true" />
                <strong>Предварительная выдача не уточнена</strong>
                <span>Поиск оборвался раньше, чем ИИ закончил сверку; причина указана выше.</span>
              </p>
              <ScanVerdictPanel key={VERDICT_PANEL_KEY} outcome={outcome.preliminary} photoUrl={photoUrl} onOpenProduct={onOpenProduct} actions={undefined} retakeLabel={retakeLabel} onRetake={undefined} />
            </>
          )}
          <RetryBar onRetry={outcome.retryable ? onRetry : undefined} onPickAnother={onPickAnother} />
        </>
      )
  }
}

function UnresolvedNote({ count }: { count: number }) {
  return (
    <p className="platform-sync__reason">
      Неполные данные: {count} позиций индекса не связаны с товарами Platform и в выдачу не попали. Это не значит, что
      других товаров нет.
    </p>
  )
}

/**
 * Поиск по фото. Всем видна одна плашка состояния поиска словом и значком;
 * звенья и проверка — служебное, в свёрнутом блоке и только с правом
 * `platform:search:test` (RP-FB2). Ищет в боевом каталоге (RP-FB27). Результат различает
 * «не нашлось» и «поиск недоступен».
 *
 * Снимок — «Сканер» (живая камера: штрихкод с потока, без него — кадр в
 * поиск по фото, как в старом клиенте, RP-FB4) и «Выбрать фото» (галерея или
 * проводник). Поиск начинается сразу, как снимок выбран; повторяет его «Повторить» под итогом.
 */
export function PhotoSearchScreen({
  vm,
  actions,
  photoUrl,
  onOpenProduct,
  sentShot = null,
  session,
  detailsExtra = null,
}: {
  /** Время поиска по этапам внутри «Посмотреть детали поиска» (администратору): узел собирает приложение, у него данные. */
  detailsExtra?: ReactNode
  /** Смена сканирования (RP-FB56): последнее событие «Фото прикреплено к …» после возврата из окон. */
  session?: ScanSessionVm | undefined
  vm: SearchVm
  actions: SearchActions
  /** Адрес главного фото карточки товара; не задан — выдача без миниатюр. */
  photoUrl?: ProductPhotoUrl
  /** Открыть ОБЩУЮ карточку найденного товара: окно «Общий товар» поверх поиска, не карточку источника. */
  onOpenProduct: OpenCommonProduct
  /** Адрес отправленного снимка — вверху выдачи (RP-FB26). */
  sentShot?: string | null
}) {
  const filesInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [preparing, setPreparing] = useState(false)
  const searching = vm.outcome.kind === 'searching'
  const hasCamera = useHasCamera()
  const cameraOff = !hasCamera

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0]
    // Сброс value — тот же снимок второй раз тоже вызовет onChange.
    event.target.value = ''
    if (picked === undefined) return
    const early = photoProblem(picked)
    if (early !== null) {
      setFile(null)
      setProblem(early)
      return
    }
    setPreparing(true)
    const ready = await preparePhoto(picked)
    setPreparing(false)
    setFile(ready)
    setProblem(null)
    actions.search(ready)
  }
  const pick = (event: ChangeEvent<HTMLInputElement>) => void onPick(event)

  return (
    <ActionInfoQuiet>
      <section aria-labelledby="platform-search-title" className="platform-sync">
        <header className="platform-sync__toolbar">
          <div className="platform-search__head">
            <ScreenInfo title={SEARCH_SCREEN_INFO.title} lines={SEARCH_SCREEN_INFO.lines} />
            <h2 id="platform-search-title">Поиск по фото</h2>
          </div>
          <span className="platform-sync__badge" data-state={vm.health.level === 'ok' ? 'on' : 'needs-preview'}>
            {HEALTH_ICON[vm.health.level]}
            {vm.health.label}
          </span>
        </header>
        {vm.health.detail !== null && <p className="platform-sync__note">{vm.health.detail}</p>}
        {vm.canDiagnose && (
          <details className="platform-search__service">
            <summary>
              Настройка поиска: состояние частей
              <ActionInfo id="platform.search.service" label="Настройка поиска" />
            </summary>
            {vm.health.components.length > 0 && (
              <ul aria-label="Звенья поиска">
                {vm.health.components.map((component) => (
                  <li key={component.name}>
                    {component.name}: {component.label}
                    {component.detail !== null && ` (${component.detail})`}
                  </li>
                ))}
              </ul>
            )}
            <div>
              <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.checkHealth} actionId="platform.search.health">
                Проверить состояние
              </Button>
            </div>
          </details>
        )}

        <div className="platform-search__pick" role="group" aria-label="Снимок товара">
          {/* Кнопка видна всегда (решение владельца 22.10): без камеры она серая и объясняет почему, а не пропадает. */}
          <Button
            variant={cameraOff ? 'secondary' : 'primary'}
            icon={cameraOff ? <VideoOff size={16} aria-hidden="true" /> : <ScanBarcode size={16} aria-hidden="true" />}
            disabled={preparing || searching || cameraOff}
            aria-disabled={preparing || searching || cameraOff}
            aria-describedby={cameraOff ? 'platform-search-no-camera' : undefined}
            data-camera={cameraOff ? 'none' : 'ok'}
            onClick={actions.openScanner}
            actionId="platform.search.scanner"
            disabledReason={cameraOff ? NO_CAMERA_REASON : null}
          >
            {cameraOff ? 'Сканер: камеры нет' : 'Сканер'}
          </Button>
          <ActionInfo id="platform.search.file" label="Выбрать фото" disabled={preparing || searching}>
            <input ref={filesInput} type="file" accept="image/*" hidden aria-label="Фото товара" onChange={pick} />
            <Button
              variant={cameraOff ? 'primary' : 'secondary'}
              icon={<Images size={16} aria-hidden="true" />}
              disabled={preparing || searching}
              onClick={() => filesInput.current?.click()}
            >
              Выбрать фото
            </Button>
          </ActionInfo>
        </div>
        {cameraOff && (
          <p id="platform-search-no-camera" className="platform-sync__note" role="status">
            На этом устройстве нет камеры — выберите фото.
          </p>
        )}
        {preparing && <p className="platform-sync__note">Готовим снимок…</p>}
        {/* Сам снимок — вверху выдачи вместе с прочитанным на нём (RP-FB26), здесь только имя файла. */}
        {file !== null && <p className="platform-sync__note">Выбрано: {file.name}</p>}
        {problem !== null && <p className="platform-sync__reason">{problem}</p>}

        {session !== undefined && session.last !== null && <ScanSessionBar vm={session} surface="page" />}
        <SearchProgressPanel onCancel={searching ? actions.cancel : undefined} shotUrl={sentShot} />
        <SearchOutcome
          outcome={vm.outcome}
          photoUrl={photoUrl}
          onOpenProduct={onOpenProduct}
          sentShot={sentShot}
          canSeeDetails={vm.isSystemAdmin}
          detailsExtra={detailsExtra}
          onRetry={actions.retry}
          onPickAnother={() => filesInput.current?.click()}
          verdictActions={actions.verdict}
          onRetake={() => filesInput.current?.click()}
        />
      </section>
    </ActionInfoQuiet>
  )
}
